<template>
  <v-content>
    <v-dialog v-model="dialog" persistent max-width="1000px">
      <v-card class="vehicle-dialog-root">
        <v-card-title class="redColor">
          <span class="headline"> {{ $t("clients.cars.add_vehicle") }}</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12" xs="12" sm="6">
                <v-autocomplete
                  v-model="brand"
                  :items="brands"
                  :loading="loadingBrand"
                  :label="$t('parts.advanced_search.brand')"
                  item-title="title"
                  flat
                  variant="outlined"
                  color="red-darken-1"
                  hide-details
                  return-object
                  clearable
                  @click:append-inner="brand = null"
                >
                  <!-- Custom dropdown item -->
                  <template #item="{ props, item }">
                    <v-list-item v-bind="props">
                      <template #prepend>
                        <v-img
                          :src="item.raw.logo"
                          height="35"
                          width="35"
                          class="mr-3"
                          cover
                        />
                      </template>
                      <!-- <v-list-item-title>{{ item.raw.title }}</v-list-item-title> -->
                    </v-list-item>
                  </template>

                  <!-- Selected value in the field -->
                  <template #selection="{ item }">
                    <div class="d-flex align-center">
                      <v-img
                        :src="item.raw.logo"
                        height="35"
                        width="35"
                        class="mr-3"
                        cover
                      />
                      <span>{{ item.raw.title }}</span>
                    </div>
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col cols="12" xs="12" sm="6">
                <v-combobox
                  v-model="model"
                  :items="models"
                  :loading="loadingModel"
                  :label="!model ? $t('parts.advanced_search.model') : ''"
                  item-title="description"
                  item-value="modelCode"
                  variant="outlined"
                  color="red-darken-1"
                  hide-details
                  return-object
                  clearable
                  class="car-search-combobox"
                  @click:append-inner="model = null"
                >
                  <!-- Dropdown items -->
                  <template #item="{ props, item }">
                    <v-list-item v-bind="props">
                      <v-list-item-content>
                        <v-row>
                          <!-- Description -->
                          <v-col
                            cols="12"
                            md="4"
                            class="bg-grey-lighten-3 mt-2"
                          >
                            <div class="d-flex flex-column">
                              <span class="caption">{{
                                $t("parts.advanced_search.model")
                              }}</span>
                              <span class="font-weight-bold">{{
                                item.raw.description
                              }}</span>
                            </div>
                          </v-col>

                          <!-- Year range -->
                          <v-col
                            cols="12"
                            md="8"
                            class="bg-grey-lighten-3 mt-2"
                          >
                            <div class="d-flex flex-column">
                              <span class="caption">{{
                                $t("parts.advanced_search.year")
                              }}</span>
                              <span class="font-weight-bold">
                                {{ formatMonthYear(item.raw.startingDate) }} -
                                {{ formatMonthYear(item.raw.endingDate) }}
                              </span>
                            </div>
                          </v-col>
                        </v-row>
                      </v-list-item-content>
                    </v-list-item>
                  </template>

                  <!-- Selected value -->
                  <template #selection="{ item }">
                    <div
                      class="car-detail-selection d-flex justify-between align-center pa-2 w-100"
                    >
                      <div class="car-detail-col d-flex flex-column pr-2">
                        <span class="caption">{{
                          $t("parts.advanced_search.model")
                        }}</span>
                        <span class="font-weight-bold">{{
                          item.raw.description
                        }}</span>
                      </div>
                      <div class="car-detail-col d-flex flex-column">
                        <span class="caption">{{
                          $t("parts.advanced_search.year")
                        }}</span>
                        <span class="font-weight-bold">
                          {{ formatMonthYear(item.raw.startingDate) }} -
                          {{ formatMonthYear(item.raw.endingDate) }}
                        </span>
                      </div>
                    </div>
                  </template>
                </v-combobox>
              </v-col>
              <v-col cols="12" xs="12" md="12" lg="12">
                <v-combobox
                  v-model="version"
                  :items="versions"
                  :loading="loadingVersion"
                  :label="!version ? $t('parts.advanced_search.version') : ''"
                  item-title="fullDescription"
                  item-value="versionCode"
                  flat
                  variant="outlined"
                  color="red-darken-1"
                  hide-details
                  return-object
                  clearable
                  class="car-search-combobox car-search-version-combobox"
                  @click:append-inner="version = null"
                >
                  <!-- Dropdown items -->
                  <template #item="{ item, props }">
                    <v-list-item v-bind="props">
                      <div
                        class="d-sm-none car-version-detail-item bg-grey-lighten-3 mt-2 pa-3 w-100"
                      >
                        <div
                          class="car-version-detail-row car-version-detail-row--dropdown"
                        >
                          <div
                            class="car-version-detail-col car-version-detail-col--main"
                          >
                            <span class="caption">{{
                              $t("parts.advanced_search.version")
                            }}</span>&nbsp;
                            <span class="font-weight-bold">{{
                              item.raw.description
                            }}</span>
                          </div>
                          <div class="car-version-detail-col">
                            <span class="caption">{{
                              $t("parts.advanced_search.year")
                            }}</span>&nbsp;
                            <span class="font-weight-bold">
                              {{ formatMonthYear(item.raw.startingDate) }} -
                              {{ formatMonthYear(item.raw.endingDate) }}
                            </span>
                          </div>
                          <div class="car-version-detail-col">
                            <span class="caption">{{
                              $t("parts.advanced_search.body")
                            }}</span>&nbsp;
                            <span class="font-weight-bold">{{
                              item.raw.numberOfDoors
                            }}</span>
                          </div>
                          <div class="car-version-detail-col">
                            <span class="caption">{{
                              $t("parts.advanced_search.power")
                            }}</span>&nbsp;
                            <span class="font-weight-bold">
                              {{ item.raw.power ? item.raw.power.kw : "" }} kw,
                            </span>
                          </div>
                          <div class="car-version-detail-col">
                            <span class="caption">{{
                              $t("parts.advanced_search.engine")
                            }}</span>&nbsp;
                            <span class="font-weight-bold">{{
                              item.raw.engineCode
                            }}</span>
                          </div>
                          <div class="car-version-detail-col">
                            <span class="caption">{{
                              $t("parts.advanced_search.supply.supply")
                            }}</span>&nbsp;
                            <span class="font-weight-bold">{{
                              getSupplyLocale(item.raw.supplyCode)
                            }}</span>
                          </div>
                        </div>
                      </div>
                    <v-row class="d-none d-sm-flex pa-3 bg-grey-lighten-3 mt-2">
                      <v-col>
                        <v-row class="d-flex flex-column">
                          <span class="caption">{{
                            $t("parts.advanced_search.version")
                          }}</span>
                          <span class="font-weight-bold">{{
                            item.raw.description
                          }}</span>
                        </v-row>
                      </v-col>
                      <v-col>
                        <v-row class="d-flex flex-column">
                          <span class="caption">{{
                            $t("parts.advanced_search.year")
                          }}</span>
                          <span class="font-weight-bold">
                            {{ formatMonthYear(item.raw.startingDate) }} -
                            {{ formatMonthYear(item.raw.endingDate) }}
                          </span>
                        </v-row>
                      </v-col>
                      <v-col>
                        <v-row class="d-flex flex-column">
                          <span class="caption">{{
                            $t("parts.advanced_search.body")
                          }}</span>
                          <span class="font-weight-bold">{{
                            item.raw.numberOfDoors
                          }}</span>
                        </v-row>
                      </v-col>
                      <v-col>
                        <v-row class="d-flex flex-column">
                          <span class="caption">{{
                            $t("parts.advanced_search.power")
                          }}</span>
                          <span class="font-weight-bold">
                            {{ item.raw.power ? item.raw.power.kw : "" }} kw,
                          </span>
                        </v-row>
                      </v-col>
                      <v-col>
                        <v-row class="d-flex flex-column">
                          <span class="caption">{{
                            $t("parts.advanced_search.engine")
                          }}</span>
                          <span class="font-weight-bold">{{
                            item.raw.engineCode
                          }}</span>
                        </v-row>
                      </v-col>
                      <v-col>
                        <v-row class="d-flex flex-column">
                          <span class="caption">{{
                            $t("parts.advanced_search.supply.supply")
                          }}</span>
                          <span class="font-weight-bold">{{
                            getSupplyLocale(item.raw.supplyCode)
                          }}</span>
                        </v-row>
                      </v-col>
                    </v-row>
                    </v-list-item>
                  </template>

                  <!--SELECTED-->
                  <template #selection="{ item }">
                    <div class="car-version-detail-selection car-detail-selection w-100 d-sm-none">
                      <div class="car-version-detail-row">
                        <div
                          class="car-version-detail-col car-version-detail-col--main"
                        >
                          <span class="caption">
                            {{ $t("parts.advanced_search.version") }}
                          </span>
                          <span class="font-weight-bold">{{
                            item.raw.description
                          }}</span>
                        </div>
                        <div class="car-version-detail-col">
                          <span class="caption">{{
                            $t("parts.advanced_search.year")
                          }}</span>
                          <span class="font-weight-bold">
                            {{ formatMonthYear(item.raw.startingDate) }} -
                            {{ formatMonthYear(item.raw.endingDate) }}
                          </span>
                        </div>
                        <div class="car-version-detail-col">
                          <span class="caption">{{
                            $t("parts.advanced_search.body")
                          }}</span>
                          <span class="font-weight-bold">{{
                            item.raw.numberOfDoors
                          }}</span>
                        </div>
                        <div class="car-version-detail-col">
                          <span class="caption">{{
                            $t("parts.advanced_search.power")
                          }}</span>
                          <span class="font-weight-bold">
                            {{ item.raw.power ? item.raw.power.kw : " " }} kw,
                          </span>
                        </div>
                        <div class="car-version-detail-col">
                          <span class="caption">{{
                            $t("parts.advanced_search.engine")
                          }}</span>
                          <span class="font-weight-bold">{{
                            item.raw.engineCode
                          }}</span>
                        </div>
                        <div class="car-version-detail-col">
                          <span class="caption">{{
                            $t("parts.advanced_search.supply.supply")
                          }}</span>
                          <span class="font-weight-bold">{{
                            getSupplyLocale(item.raw.supplyCode)
                          }}</span>
                        </div>
                      </div>
                    </div>
                  <v-row class="pa-2 d-none d-sm-flex">
                    <v-col>
                      <v-row class="d-flex flex-column">
                        <span class="caption">
                          {{ $t("parts.advanced_search.version") }}
                        </span>
                        <span class="font-weight-bold">{{
                          item.raw.description
                        }}</span>
                      </v-row>
                    </v-col>
                    <v-col>
                      <v-row class="d-flex flex-column">
                        <span class="caption">{{
                          $t("parts.advanced_search.year")
                        }}</span>
                        <span class="font-weight-bold">
                          {{ formatMonthYear(item.raw.startingDate) }} -
                          {{ formatMonthYear(item.raw.endingDate) }}
                        </span>
                      </v-row>
                    </v-col>
                    <v-col>
                      <v-row class="d-flex flex-column">
                        <span class="caption">{{
                          $t("parts.advanced_search.body")
                        }}</span>
                        <span class="font-weight-bold">{{
                          item.raw.numberOfDoors
                        }}</span>
                      </v-row>
                    </v-col>
                    <v-col>
                      <v-row class="d-flex flex-column">
                        <span class="caption">{{
                          $t("parts.advanced_search.power")
                        }}</span>
                        <span class="font-weight-bold">
                          {{ item.raw.power ? item.raw.power.kw : " " }} kw,
                        </span>
                      </v-row>
                    </v-col>
                    <v-col>
                      <v-row class="d-flex flex-column">
                        <span class="caption">{{
                          $t("parts.advanced_search.engine")
                        }}</span>
                        <span class="font-weight-bold">{{
                          item.raw.engineCode
                        }}</span>
                      </v-row>
                    </v-col>
                    <v-col>
                      <v-row class="d-flex flex-column">
                        <span class="caption">{{
                          $t("parts.advanced_search.supply.supply")
                        }}</span>
                        <span class="font-weight-bold">{{
                          getSupplyLocale(item.raw.supplyCode)
                        }}</span>
                      </v-row>
                    </v-col>
                  </v-row>
                  </template>
                </v-combobox>
              </v-col>
              <v-col cols="6" xs="12" sm="6">
                <v-text-field
                  v-model="vin"
                  :label="$t('clients.cars.vin')"
                  solo
                  variant="outlined"
                  color="red-darken-1"
                />
              </v-col>
              <v-col cols="6" xs="12" sm="6">
                <v-text-field
                  v-model="notes"
                  :label="$t('clients.cars.notes')"
                  solo
                  variant="outlined"
                  color="red-darken-1"
                />
              </v-col>
              <v-col cols="12" xs="12" md="12" lg="12">
                <v-row class="justify-center">
                  <v-btn color="primary" @click="openSpecDialog">
                    {{ $t("clients.cars.specifications") }}
                    <v-icon end> mdi-plus </v-icon>
                  </v-btn>
                </v-row>
              </v-col>
              <v-col>
                <v-row
                  v-if="specifications && specifications.length"
                  class="justify-center"
                >
                  <v-col
                    v-for="(item, index) in specifications"
                    :key="index"
                    cols="auto"
                    class="d-flex align-center"
                  >
                    <v-card
                      class="pa-2 px-6 d-flex align-center"
                      elevation="0"
                      variant="outlined"
                    >
                      <span class="mr-2"
                        >{{ item.key }} : {{ item.value }}</span
                      >
                      <v-btn
                        icon
                        size="small"
                        color="error"
                        @click="removeSpec(index)"
                      >
                        <v-icon>mdi-minus-circle-outline</v-icon>
                      </v-btn>
                    </v-card>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="red-darken-1" variant="outlined" @click="close">
            {{ $t("clients.form.close") }}
          </v-btn>
          <v-btn
            :disabled="!brand || !model || !version"
            color="green-darken-1"
            variant="outlined"
            @click="save"
          >
            {{ $t("clients.form.save") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="specDialog" max-width="400px" persistent>
      <v-card>
        <v-card-title>
          <span class="subheading">
            {{ $t("clients.cars.add_specifications") }}
          </span>
        </v-card-title>
        <v-card-text>
          <v-row class="justify-center">
            <v-col xs="6" md="6" lg="6">
              <v-text-field
                v-model="specification.key"
                color="red-darken-1"
                variant="outlined"
                :label="$t('clients.cars.name')"
              />
            </v-col>
            <v-col xs="6" md="6" lg="6">
              <v-text-field
                v-model="specification.value"
                color="red-darken-1"
                variant="outlined"
                :label="$t('clients.cars.value')"
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="red-darken-1"
            variant="outlined"
            @click="closeSpecDialog"
          >
            {{ $t("clients.form.close") }}
          </v-btn>
          <v-btn color="green-darken-1" variant="outlined" @click="saveSpec">
            {{ $t("clients.form.save") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-content>
</template>

<script>
import moment from "moment";
import getRequest from "../../api/getRequest";

export default {
  filters: {
    moment(date) {
      moment.locale(localStorage.getItem("language"));
      const valid = moment(date).isValid();
      if (valid) return moment(date).format("MM/YYYY");
      return "";
    },
  },
  data: () => ({
    dialog: false,
    specDialog: false,
    client: null,
    loadingBrand: false,
    loadingModel: false,
    loadingVersion: false,
    brand: null,
    brands: [],
    model: null,
    models: [],
    version: null,
    versions: [],
    vin: "",
    notes: "",
    specification: { key: null, value: null },
    specifications: [],
  }),
  watch: {
    brand(value) {
      if (value) {
        this.models = [];
        this.model = null;
        this.versions = [];
        this.version = null;
        this.getModels(value.brandCode);
      }
    },
    model(value) {
      if (value) {
        this.versions = [];
        this.version = null;
        this.getVersions(value.brandCode, value.modelCode);
      }
    },
  },
  methods: {
    open(client) {
      this.dialog = true;
      this.getBrands();
    },
    close() {
      this.dialog = false;
      this.client = null;
      this.brand = null;
      this.brand = [];
      this.model = null;
      this.models = [];
      this.version = null;
      this.versions = [];
      this.vin = "";
      this.notes = "";
      this.specification = { key: null, value: null };
      this.specifications = [];
      this.$emit("close");
    },
    save() {
      this.$emit("save", {
        brand: this.brand,
        model: this.model,
        version: this.version,
        vin: this.vin,
        notes: this.notes,
        specifications: this.specifications,
      });
      this.close();
    },
    getBrands() {
      this.loadingBrand = true;
      getRequest("/info-car/brands")
        .then((response) => {
          this.brands = response.brands;
        })
        .catch(() => {})
        .finally(() => {
          this.loadingBrand = false;
        });
    },
    getModels(brandCode) {
      this.loadingModel = true;
      getRequest("/info-car/models", `brand_code=${brandCode}`)
        .then((response) => {
          this.models = response.models;
        })
        .catch(() => {})
        .finally(() => {
          this.loadingModel = false;
        });
    },
    getVersions(brandCode, modelCode) {
      this.loadingVersion = true;
      getRequest(
        "/info-car/versions",
        `brand_code=${brandCode}&model_code=${modelCode}`,
      )
        .then((response) => {
          this.versions = response.versions;
        })
        .catch(() => {})
        .finally(() => {
          this.loadingVersion = false;
        });
    },
    openSpecDialog() {
      this.specDialog = true;
    },
    closeSpecDialog() {
      this.specDialog = false;
      this.specification = { key: null, value: null };
    },
    saveSpec() {
      this.specifications.push(this.specification);
      this.closeSpecDialog();
    },
    removeSpec(index) {
      this.specifications.splice(index, 1);
    },
    getSupplyLocale(supply) {
      switch (supply) {
        case "B":
          return this.$t("parts.advanced_search.supply.petrol");
        case "D":
          return this.$t("parts.advanced_search.supply.diesel");
        case "G":
          return this.$t("parts.advanced_search.supply.gas");
        default:
          return supply;
      }
    },
    formatMonthYear(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      const month = String(date.getUTCMonth() + 1).padStart(2, "0"); // months are 0-indexed
      const year = date.getUTCFullYear();
      return `${month}/${year}`;
    },
  },
};
</script>

<style scoped>
/* Model/version combobox mobile layout */
@media (max-width: 959px) {
  .vehicle-dialog-root .car-version-detail-selection {
    width: 100%;
    padding: 0.375rem 0.25rem;
  }

  .vehicle-dialog-root .car-version-detail-row {
    display: flex;
    flex-wrap: nowrap;
    align-items: flex-start;
    width: 100%;
    gap: 0.75rem 1rem;
  }

  .vehicle-dialog-root .car-version-detail-col {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    min-width: 0;
    flex: 1 1 0;
  }

  .vehicle-dialog-root .car-version-detail-col .caption {
    line-height: 1.25;
    margin-bottom: 0.125rem;
  }

  .vehicle-dialog-root .car-version-detail-col .font-weight-bold {
    line-height: 1.4;
    word-break: break-word;
  }

  .vehicle-dialog-root .car-search-combobox :deep(.v-field) {
    --v-field-padding-top: 10px;
    --v-field-padding-bottom: 10px;
    height: auto !important;
    min-height: 3.5rem;
  }

  .vehicle-dialog-root .car-search-version-combobox :deep(.v-field) {
    --v-field-padding-top: 12px;
    --v-field-padding-bottom: 12px;
    min-height: 4.5rem;
  }

  .vehicle-dialog-root .car-search-combobox :deep(.v-field__field) {
    height: auto !important;
    min-height: 3.5rem;
    align-items: flex-start;
  }

  .vehicle-dialog-root .car-search-version-combobox :deep(.v-field__field) {
    min-height: 4.5rem;
  }

  .vehicle-dialog-root .car-search-combobox :deep(.v-field__input) {
    white-space: normal;
    overflow: visible;
    align-items: flex-start;
    flex-wrap: wrap;
    min-height: unset;
    padding-top: 8px;
    padding-bottom: 8px;
  }

  .vehicle-dialog-root .car-search-version-combobox :deep(.v-field__input) {
    display: block !important;
    height: auto !important;
    padding-top: 10px;
    padding-bottom: 10px;
  }

  .vehicle-dialog-root .car-search-combobox :deep(.v-combobox__selection) {
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
    width: 100%;
    max-width: 100%;
  }

  .vehicle-dialog-root .car-search-version-combobox :deep(.v-combobox__selection) {
    display: block !important;
    width: 100%;
    position: static !important;
    transform: none !important;
  }

  .vehicle-dialog-root .car-detail-selection {
    overflow: visible;
    width: 100%;
  }


  .vehicle-dialog-root .car-version-detail-row,
  .vehicle-dialog-root .car-version-detail-row--dropdown {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.75rem 0.875rem;
    flex-wrap: unset;
  }

  .vehicle-dialog-root .car-version-detail-col {
    flex: unset;
    padding: 0;
  }

  .vehicle-dialog-root .car-version-detail-col--main {
    grid-column: 1 / -1;
    padding-bottom: 0.625rem;
    margin-bottom: 0.25rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  }

  .vehicle-dialog-root .car-version-detail-col--main .font-weight-bold {
    line-height: 1.45;
  }

  .vehicle-dialog-root .car-detail-col {
    flex: 1 1 calc(50% - 0.5rem);
    max-width: none;
    min-width: 0;
    padding: 0;
    word-break: break-word;
  }

  .vehicle-dialog-root .car-detail-selection.d-flex {
    flex-wrap: wrap;
    gap: 0.625rem 0.75rem;
  }
}
</style>
